import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { fetchData } from '../../../../helpers/fetchData'
import CustomTable from '../../../../shared/CustomTable'
import CustomPagination from '../../../../shared/CustomPagination'
import CustomButton from '../../../../shared/CustomButton'
import { InsuranceClaimInvoiceColumns } from '../../../../constants/BillingConstants'
import DropdownField from '../../../../shared/DropdownField'
import DatePicker from '../../../../shared/DatePicker'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { EditIcon, PrintIcon } from '../../../../utils/TableActionIcons'
import { HiArrowUturnLeft } from 'react-icons/hi2'
import { IoIosSend } from 'react-icons/io'
import { FaExclamationCircle, FaHistory } from 'react-icons/fa'
import ReusableModal from '../../../../shared/ReuseableModal'
import MarkInvoiceReturnedForm from './MarkInvoiceReturnedForm'
import ConfirmationModal from '../../../../shared/ConfirmationModal'
import ZatcaHistoryModal from '../ZatcaHistoryModal'
import { useGetInsuranceClaimInvoicesQuery } from '../../../../redux/api/modules/billingApi'
import CheckPermission from '../../../../hooks/CheckPermission'

const InsuranceClaimInvoiceList = () => {
  const { t } = useTranslation()
  const { hasPermission } = CheckPermission()
  const navigate = useNavigate()
  // Pagination and Filters State
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total_pages: 1,
    total_record: 0
  })

  const [filters, setFilters] = useState({
    payer_id: '',
    branch_id: '',
    status: '',
    start_date: '',
    end_date: '',
    query: ''
  })
  const [appliedFilters, setAppliedFilters] = useState(filters)

  const queryPayload = React.useMemo(() => ({
    ...appliedFilters,
    page: pagination.page,
    per_page: pagination.per_page
  }), [appliedFilters, pagination.page, pagination.per_page])

  const {
    data: responseData,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetInsuranceClaimInvoicesQuery(queryPayload)

  const loading = queryLoading || queryFetching
  const data = responseData?.data || []

  const [payers, setPayers] = useState([])
  const [openReturnModal, setOpenReturnModal] = useState(false)
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null)
  const [returnLoading, setReturnLoading] = useState(false)
  const [sendLoading, setSendLoading] = useState(false)
  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false)
  const [failedInvoiceData, setFailedInvoiceData] = useState(null)
  const [openZatcaHistoryModal, setOpenZatcaHistoryModal] = useState(false)
  const [zatcaHistoryData, setZatcaHistoryData] = useState(null)
  const [zatcaHistoryLoading, setZatcaHistoryLoading] = useState(false)
  const [selectedHistoryInvoice, setSelectedHistoryInvoice] = useState(null)

  useEffect(() => {
    fetchPayers()
  }, [])

  const fetchPayers = async () => {
    try {
      const res = await fetchData('POST', '/v3/payer/get', { category: '' })
      if (res?.data) {
        setPayers(
          res.data.map(p => ({
            label: p.name || p.name_en || p.payer_name,
            value: p.payer_id
          }))
        )
      }
    } catch (error) {
      console.error('Error fetching payers:', error)
    }
  }

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }))
    setAppliedFilters(filters)
  }

  const handleResetFilters = () => {
    const resetFilters = {
      payer_id: '',
      branch_id: '',
      status: '',
      start_date: '',
      end_date: '',
      query: ''
    }
    setFilters(resetFilters)
    setPagination(prev => ({ ...prev, page: 1 }))
    setAppliedFilters(resetFilters)
  }

  const handleMarkReturned = async data => {
    if (!selectedInvoiceData?.id) return
    setReturnLoading(true)
    try {
      const res = await fetchData(
        'PUT',
        `/v3/invoices/insurance_claim_invoices/${selectedInvoiceData.id}/mark_returned`,
        {
          return_reason: data.return_reason
        }
      )
      if (res) {
        toast.success(
          res?.message || t('Invoice marked as returned successfully')
        )
        setSelectedInvoiceData(null)
        setOpenReturnModal(false)
        refetch()
      }
    } catch (error) {
      toast.error(error?.message || t('Failed to mark invoice as returned'))
    } finally {
      setReturnLoading(false)
    }
  }

  const handleSendInvoice = async () => {
    if (!hasPermission('billing', 'update')) return
    if (!selectedInvoiceData?.id) return

    if (!selectedInvoiceData?.invoice_no) {
      toast.error(t('Invoice No is required'))
      return
    }

    setSendLoading(true)
    try {
      const res = await fetchData(
        'POST',
        '/v3/zatca/sendFinalInsuranceReportInvoice',
        {
          invoice_no: selectedInvoiceData.invoice_no
        }
      )
      if (res) {
        toast.success(res?.message || t('Invoice sent successfully'))
      }
    } catch (error) {
      toast.error(error?.message || t('Failed to send invoice'))
    } finally {
      refetch()
      setSendLoading(false)
      setShowSendConfirmModal(false)
      setSelectedInvoiceData(null)
    }
  }

  const getZatcaHistory = async invoiceNumber => {
    setOpenZatcaHistoryModal(true)
    setZatcaHistoryLoading(true)
    setSelectedHistoryInvoice(invoiceNumber)
    try {
      const response = await fetchData(
        'GET',
        `/v3/zatca/history/insurance/${invoiceNumber}`
      )
      if (response?.success) {
        setZatcaHistoryData(response)
      } else {
        toast.error(response?.message || t('Failed to fetch ZATCA history'))
        setOpenZatcaHistoryModal(false)
      }
    } catch (error) {
      toast.error(t('Failed to fetch ZATCA history'))
      setOpenZatcaHistoryModal(false)
    } finally {
      setZatcaHistoryLoading(false)
    }
  }

  const columns = [
    {
      header: t('Actions'),
      body: row => (
        <div className='flex gap-2'>
          {/* <HiArrowUturnLeft
            title={t('Mark as Returned')}
            className='text-red-500 cursor-pointer hover:text-red-600'
            size={18}
            onClick={() => {
              setSelectedInvoiceData(row)
              setOpenReturnModal(true)
            }}
          /> */}
          {hasPermission('billing', 'update') && (
            <EditIcon
              title={t('Edit')}
              iconSize={17}
              onClick={() =>
                navigate(`/billing/insurance-claim-invoice/${row.id}`, {
                  state: {
                    edit: true,
                    path: '/billing',
                    module: 6
                  }
                })
              }
            />
          )}
            <PrintIcon
              title={t('Print')}
              iconSize={17}
              onClick={() =>
                navigate('/billing/insurance-claim-invoice/print', {
                  state: {
                    invoiceId: row.id,
                    path: '/billing',
                    module: 6
                  }
                })
              }
            />
          {row?.zatca_status === 'FAILED' && hasPermission('billing', 'get') && (
            <FaExclamationCircle
              title={t('View Failure Reason')}
              className='text-red-600 cursor-pointer hover:text-red-700'
              size={17}
              onClick={() => setFailedInvoiceData(row)}
            />
          )}
          {(row?.zatca_status === 'REPORTED' || row?.zatca_status === 'CLEARED') && hasPermission('billing', 'get') && (
            <FaHistory
              title={t('ZATCA History')}
              className='text-teal-500 cursor-pointer hover:text-teal-600'
              size={16}
              onClick={() => getZatcaHistory(row?.invoice_no)}
            />
          )}
          {row?.zatca_status !== 'REPORTED' && row?.zatca_status !== 'CLEARED' && hasPermission('billing', 'update') && (
            <IoIosSend
              title={t('Send Invoice')}
              className='text-primary cursor-pointer hover:text-blue-700'
              size={18}
              onClick={() => {
                setSelectedInvoiceData(row)
                setShowSendConfirmModal(true)
              }}
            />
          )}
        </div>
      )
    },
    ...InsuranceClaimInvoiceColumns(t)
  ]

  return (
    <div className='w-full'>
      <div className='flex gap-3 mb-4 flex-wrap items-start'>
        <div className='flex-none w-[180px]'>
          <DatePicker
            placeholder={t('StartDate')}
            value={filters.start_date ? new Date(filters.start_date) : null}
            onChange={e =>
              setFilters(prev => ({
                ...prev,
                start_date: moment(e.value).format('YYYY-MM-DD')
              }))
            }
            useFormik={false}
          />
        </div>
        <div className='flex-none w-[180px]'>
          <DatePicker
            placeholder={t('EndDate')}
            value={filters.end_date ? new Date(filters.end_date) : null}
            onChange={e =>
              setFilters(prev => ({
                ...prev,
                end_date: moment(e.value).format('YYYY-MM-DD')
              }))
            }
            useFormik={false}
          />
        </div>
        <div className='flex-none w-[180px]'>
          <DropdownField
            placeholder={t('Payer')}
            options={payers}
            value={filters.payer_id}
            onChange={e => setFilters(prev => ({ ...prev, payer_id: e.value }))}
            useFormik={false}
            filter
          />
        </div>
        <div className='flex-none flex gap-2'>
          <CustomButton
            title={t('ApplyFilters')}
            icon='pi pi-filter'
            buttonType='primary'
            onClick={handleApplyFilters}
            className='w-fit h-[35px]'
          />
          <CustomButton
            title={t('Reset')}
            icon='pi pi-sync'
            buttonType='reset'
            onClick={handleResetFilters}
            className='w-fit h-[35px]'
          />
        </div>
      </div>

      <CustomTable isLoading={loading} data={data} columns={columns} />

      {data?.length > 0 && !loading && (
        <CustomPagination
          currentPage={responseData?.page || pagination.page}
          rowsPerPage={responseData?.per_page || pagination.per_page}
          totalRecords={responseData?.total_record || responseData?.totalRecords || 0}
          totalPages={responseData?.total_pages || responseData?.totalPages || 1}
          onPageChange={(page, per_page) => {
            setPagination(prev => ({ ...prev, page, per_page }))
          }}
        />
      )}

      <ReusableModal
        isVisible={openReturnModal}
        title={t(`Mark Invoice as Returned`)}
        HeaderIcon={HiArrowUturnLeft}
        position='top'
        className='w-full md:w-[600px]'
        onClose={() => {
          setSelectedInvoiceData(null)
          setOpenReturnModal(false)
          setReturnLoading(false)
        }}
      >
        <MarkInvoiceReturnedForm
          handleMarkReturned={handleMarkReturned}
          loading={returnLoading}
          selectedInvoiceData={selectedInvoiceData}
        />
      </ReusableModal>

      <ConfirmationModal
        visible={showSendConfirmModal}
        setVisible={setShowSendConfirmModal}
        header='Send Invoice'
        message='Are you sure you want to send this invoice? This action will submit the insurance claim invoice for processing.'
        acceptLabel='Send'
        rejectLabel='Cancel'
        onAccept={handleSendInvoice}
        onReject={() => {
          setShowSendConfirmModal(false)
          setSelectedInvoiceData(null)
        }}
        isLoading={sendLoading}
      />

      <ReusableModal
        isVisible={!!failedInvoiceData}
        title={t('Failure Reason')}
        HeaderIcon={FaExclamationCircle}
        position='top'
        className='w-full md:w-[620px]'
        onClose={() => setFailedInvoiceData(null)}
      >
        <div className='space-y-3'>
          <div className='text-sm text-gray-600'>
            <span className='font-semibold text-gray-800'>
              {t('Invoice Number')}:
            </span>{' '}
            {failedInvoiceData?.invoice_no || '-'}
          </div>
          <div className='rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 whitespace-pre-wrap break-words'>
            {failedInvoiceData?.zatca_error_message ||
              t('No failure reason provided')}
          </div>
        </div>
      </ReusableModal>

      <ReusableModal
        isVisible={openZatcaHistoryModal}
        title={t(`ZATCA Submission History Of #${selectedHistoryInvoice}`)}
        HeaderIcon={FaHistory}
        position='top'
        onClose={() => {
          setOpenZatcaHistoryModal(false)
          setZatcaHistoryData(null)
          setSelectedHistoryInvoice(null)
        }}
        className='w-full md:w-[90%] lg:w-[80%]'
      >
        <ZatcaHistoryModal
          historyData={zatcaHistoryData}
          loading={zatcaHistoryLoading}
        />
      </ReusableModal>
    </div>
  )
}

export default InsuranceClaimInvoiceList
